/**
 * Retroreflection — a light-return lobe for sign films, road paint and safety
 * bands (shader additions wave, 2026-09-15). The idea is the 2026 "Minimal
 * Retroreflective Microfacet Model": re-use a microfacet BRDF with the VIEW
 * vector reflected about the normal. three r185 ships no retroreflectivity
 * control (that arrives in a later revision), so this is a MODIFIER on the
 * emissive slot, driven by one explicit light.
 *
 * Reflecting V about N flips its tangential part:
 *     V_r = 2N(N·V) − V           (= reflect(−V, N); reflect(V, N) points INTO the surface)
 * A microfacet lobe evaluated with V_r peaks when its half vector
 * H_r = normalize(V_r + L) aligns with N — i.e. when L ≈ V: the sign lights up
 * exactly when the light sits at the viewer, and falls off with the
 * observation angle through the same NDF. A GGX D with the material's
 * roughness gives that falloff; it is normalised by α² so the PEAK is bounded
 * (a mirror-smooth film does not produce an unbounded spike), scaled by the
 * incident light (colour × N·L) and by `strength`.
 *
 * SCOPE — direct light only. One light (the sun, or a headlight) reaches the
 * lobe through the handle's VIEW-space direction and colour; there is no
 * shadow term (the emissive slot has none) and no environment
 * retroreflection (a prefiltered cubemap assumes an ordinary reflection
 * kernel). A first coherent scope, stated as such.
 */
import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec3, float, normalView, positionViewDirection, normalize, saturate, materialRoughness,
} from 'three/tsl';

export interface V3 { x: number; y: number; z: number }

export interface RetroreflectionConfig {
  /** 0..1 (default 0.8). */
  strength?: number;
  /** Direction TOWARD the light, VIEW space (default: from the camera). */
  lightDirView?: [number, number, number];
  lightColor?: [number, number, number];
  /** Roughness for the lobe; null (default) reads the material's. */
  roughness?: number | null;
  /** Optional 0..1 mask node (a texture channel); default 1. */
  mask?: unknown;
}

export interface RetroreflectionHandle {
  strength: { value: number };
  lightDirView: { value: THREE.Vector3 };
  lightColor: { value: THREE.Vector3 };
}

const dot = (a: V3, b: V3): number => a.x * b.x + a.y * b.y + a.z * b.z;
const norm = (a: V3): V3 => { const l = Math.hypot(a.x, a.y, a.z) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; };

/** V reflected about N: 2N(N·V) − V. */
export function retroViewCpu(n: V3, v: V3): V3 {
  const d = dot(n, v);
  return { x: 2 * n.x * d - v.x, y: 2 * n.y * d - v.y, z: 2 * n.z * d - v.z };
}

/** GGX normal distribution D(n·h) for α. */
export function ggxDCpu(nDotH: number, alpha: number): number {
  const a2 = alpha * alpha;
  const d = nDotH * nDotH * (a2 - 1) + 1;
  return a2 / (Math.PI * d * d);
}

/**
 * The retro lobe scalar: 4·α²·D(N·H_r)·(N·L), bounded to 4 — peaks near 1.27
 * for any roughness when L ≈ V, falls off with the observation angle.
 */
export function retroLobeCpu(n: V3, v: V3, l: V3, roughness: number): number {
  const nn = norm(n), vv = norm(v), ll = norm(l);
  const ndl = Math.max(0, dot(nn, ll));
  if (ndl <= 0) return 0;
  const vr = retroViewCpu(nn, vv);
  const h = norm({ x: vr.x + ll.x, y: vr.y + ll.y, z: vr.z + ll.z });
  const ndh = Math.max(0, Math.min(1, dot(nn, h)));
  const alpha = Math.max(0.05, roughness * roughness);
  return Math.min(4, 4 * alpha * alpha * ggxDCpu(ndh, alpha) * ndl);
}

export function applyRetroreflection(
  material: THREE.Material & { emissiveNode?: unknown; needsUpdate?: boolean },
  config?: RetroreflectionConfig,
): RetroreflectionHandle {
  const ld = config?.lightDirView ?? [0, 0, 1];
  const lc = config?.lightColor ?? [1, 1, 1];
  const uStrength = uniform(config?.strength ?? 0.8);
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uLightColor = uniform(vec3(lc[0], lc[1], lc[2]));
  const rOverride = config?.roughness ?? null;
  const mask = config?.mask ?? null;

  const prev = material.emissiveNode;
  material.emissiveNode = Fn(() => {
    const n = normalView;
    const v = positionViewDirection;             // toward the camera
    const l = normalize(uLightDir);
    const ndl = saturate(n.dot(l));
    const vr = n.mul(n.dot(v).mul(2)).sub(v);    // V reflected about N
    const h = normalize(vr.add(l));
    const ndh = saturate(n.dot(h));
    const r = float(rOverride !== null ? rOverride : (materialRoughness as never));
    const alpha = r.mul(r).max(0.05);
    const a2 = alpha.mul(alpha);
    const dd = ndh.mul(ndh).mul(a2.sub(1)).add(1);
    const D = a2.div(dd.mul(dd).mul(Math.PI));
    let lobe = D.mul(a2).mul(4).mul(ndl).min(4).mul(uStrength);
    if (mask) lobe = lobe.mul(float(mask as never));
    const base = (prev ?? vec3(0, 0, 0)) as never;
    return vec3(base).add(uLightColor.mul(lobe));
  })();
  material.needsUpdate = true;

  return {
    strength: uStrength as unknown as { value: number },
    lightDirView: uLightDir as unknown as { value: THREE.Vector3 },
    lightColor: uLightColor as unknown as { value: THREE.Vector3 },
  };
}
